import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { FaApple } from 'react-icons/fa';
import { SiAndroid, SiFirefoxbrowser, SiGooglechrome } from 'react-icons/si';
import { FiMonitor, FiTerminal } from 'react-icons/fi';
import Logo from './partials/Logo';
import { isHashNavigationEnabled, scrollToSection, buildUrlWithHash } from '../../utils/navigationUtils';
import { redirectToMobileStore } from '../../../../util/mobileStoreRedirect';

// Warm the main app chunk when an anonymous visitor shows login intent, so
// the post-login navigation into the app doesn't wait on a network fetch.
// (The idle-time prefetch in Routes.js only runs for visitors with a token.)
let appChunkPrefetched = false;
const prefetchAppChunk = () => {
    if (appChunkPrefetched) return;
    appChunkPrefetched = true;
    import('../../../Home');
};


const propTypes = {
    navPosition: PropTypes.string,
    hideNav: PropTypes.bool,
    hideSignin: PropTypes.bool,
    bottomOuterDivider: PropTypes.bool,
    bottomDivider: PropTypes.bool
};

const defaultProps = {
    navPosition: '',
    hideNav: false,
    hideSignin: false,
    bottomOuterDivider: false,
    bottomDivider: false
};

const USE_CASES_LINKS = [
    { label: 'Message yourself', to: '/use-cases/message-yourself' },
    { label: 'Save from the web', to: '/use-cases/save-from-web' },
    { label: 'Read it later', to: '/use-cases/read-it-later' },
    { label: 'Second brain', to: '/use-cases/second-brain' },
    { label: 'Screenshots', to: '/use-cases/screenshots' },
    { label: 'All use cases', to: '/use-cases' },
    { label: 'Who it\'s for', to: '/for' }
];

const COMPARE_LINKS = [
    { label: 'vs Pocket', to: '/compare/pocket-alternatives' },
    { label: 'vs Raindrop', to: '/compare/raindrop-alternative' },
    { label: 'vs Notion', to: '/compare/notion-alternative' },
    { label: 'vs Evernote', to: '/compare/evernote-alternative' },
    { label: 'vs mymind', to: '/compare/mymind-alternative' },
    { label: 'vs Glasp', to: '/compare/glasp-alternative' },
    { label: 'All comparisons', to: '/compare' }
];

const DOWNLOAD_LINKS = [
    {
        label: 'Web Clipper',
        description: 'Save links, images, and snippets without leaving the page.',
        to: '/download/browser-extension',
        icons: [SiGooglechrome, SiFirefoxbrowser]
    },
    {
        label: 'Luckynote for mobile',
        description: 'Capture ideas on the go and keep everything in sync.',
        to: '/download/mobile',
        icons: [FaApple, SiAndroid]
    },
    {
        label: 'Luckynote desktop',
        description: 'Search and save from anywhere on your Mac.',
        to: '/download-app',
        icons: [FaApple, FiMonitor]
    },
    {
        label: 'Luckynote CLI',
        description: 'Search, save, and use your library from the terminal.',
        to: '/download/cli',
        icons: [FiTerminal]
    }
];

const DropdownChevron = () => (
    <svg
        className="header-nav-chevron"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        aria-hidden="true"
    >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 9l6 6 6-6" />
    </svg>
);

const Header = ({ className, navPosition, hideNav, hideSignin, bottomOuterDivider, bottomDivider, ...props }) => {
    const [isActive, setIsactive] = useState(false);
    const [openMobileSubmenu, setOpenMobileSubmenu] = useState(null);
    const location = useLocation();
    const history = useHistory();
    const isMobile = useMediaQuery({ maxWidth: 820 });

    const nav = useRef(null);
    const hamburger = useRef(null);

    useEffect(() => {
        isActive && openMenu();
        document.addEventListener('keydown', keyPress);
        document.addEventListener('click', clickOutside);
        return () => {
            document.removeEventListener('keydown', keyPress);
            document.removeEventListener('click', clickOutside);
        };
    });

    useEffect(() => {
        if (!isMobile && isActive) {
            closeMenu();
        }
    }, [isMobile, isActive]);

    const openMenu = () => {
        document.body.classList.add('off-nav-is-active');
        nav.current.style.maxHeight = nav.current.scrollHeight + 'px';
        setIsactive(true);
    };

    const closeMenu = () => {
        document.body.classList.remove('off-nav-is-active');
        nav.current && (nav.current.style.maxHeight = null);
        setIsactive(false);
        setOpenMobileSubmenu(null);
    };

    const keyPress = e => {
        isActive && e.keyCode === 27 && closeMenu();
    };

    const clickOutside = e => {
        if (!nav.current) return;
        if (!isActive || nav.current.contains(e.target) || e.target === hamburger.current) return;
        closeMenu();
    };

    // Handler for smooth scrolling to hash sections while preserving query parameters
    // Future-proof: Works with current landing page, easily extensible for other pages
    const handleHashClick = (e, sectionId, trackingEvent = null) => {
        e.preventDefault();
        
        // Close mobile menu if open
        closeMenu();
        
        // Check if current page supports hash navigation
        const supportsHashNav = isHashNavigationEnabled(location.pathname);
        
        // If we're not on a page that supports hash navigation, navigate to landing page
        if (!supportsHashNav) {
            // Preserve query params when redirecting to landing page
            const search = location.search;
            history.push(buildUrlWithHash('/', search, sectionId));
            return;
        }
        
        // If we're on a page that supports hash navigation, scroll smoothly
        const newUrl = buildUrlWithHash(location.pathname, location.search, sectionId);
        
        // Update URL hash without reloading, preserving query params
        window.history.replaceState({}, document.title, newUrl);
        
        // Scroll to section with offset
        scrollToSection(sectionId);
    };

    const handleTrackAndClose = (trackingEvent = null) => {
        closeMenu();
    };

    const toggleMobileSubmenu = (event, submenu) => {
        if (!isMobile) return false;

        event.preventDefault();
        setOpenMobileSubmenu(currentSubmenu => currentSubmenu === submenu ? null : submenu);
        return true;
    };

    const classes = classNames('site-header', bottomOuterDivider && 'has-bottom-divider', className);

    return (
        <header {...props} className={classes} data-nav-position={navPosition || undefined}>
            <div className="container site-header-container">
                <div className={classNames('site-header-inner', bottomDivider && 'has-bottom-divider')}>
                    <Logo />
                    {!hideNav && (
                        <>
                            <button
                                ref={hamburger}
                                className="header-nav-toggle"
                                onClick={isActive ? closeMenu : openMenu}
                            >
                                <span className="screen-reader">Menu</span>
                                <span className="hamburger">
                                    <span className="hamburger-inner"></span>
                                </span>
                            </button>
                            <nav ref={nav} className={classNames('header-nav', isActive && 'is-active')}>
                                <div className="header-nav-inner">
                                    <ul className="list-reset text-xs header-nav-center">
                                        <li className={classNames(isMobile && openMobileSubmenu === 'use-cases' && 'is-mobile-submenu-open')}>
                                            <Link
                                                to="/use-cases"
                                                className="header-nav-trigger"
                                                style={{ fontWeight: '600' }}
                                                aria-haspopup="true"
                                                aria-expanded={isMobile ? openMobileSubmenu === 'use-cases' : undefined}
                                                onClick={(event) => {
                                                    if (toggleMobileSubmenu(event, 'use-cases')) return;
                                                    handleTrackAndClose('UseCasesClick');
                                                }}
                                            >
                                                Use cases
                                                <DropdownChevron />
                                            </Link>
                                            <ul className="header-submenu list-reset" aria-label="Use cases">
                                                {USE_CASES_LINKS.map(({ label, to }) => (
                                                    <li key={to}>
                                                        <Link to={to} onClick={closeMenu}>
                                                            {label}
                                                        </Link>
                                                    </li>
                                                ))}
                                            </ul>
                                        </li>
                                        <li className={classNames(isMobile && openMobileSubmenu === 'compare' && 'is-mobile-submenu-open')}>
                                            <Link
                                                to="/compare"
                                                className="header-nav-trigger"
                                                style={{ fontWeight: '600' }}
                                                aria-haspopup="true"
                                                aria-expanded={isMobile ? openMobileSubmenu === 'compare' : undefined}
                                                onClick={(event) => {
                                                    if (toggleMobileSubmenu(event, 'compare')) return;
                                                    handleTrackAndClose();
                                                }}
                                            >
                                                Compare
                                                <DropdownChevron />
                                            </Link>
                                            <ul className="header-submenu list-reset" aria-label="Compare">
                                                {COMPARE_LINKS.map(({ label, to }) => (
                                                    <li key={to}>
                                                        <Link to={to} onClick={closeMenu}>
                                                            {label}
                                                        </Link>
                                                    </li>
                                                ))}
                                            </ul>
                                        </li>
                                        <li className={classNames(isMobile && openMobileSubmenu === 'download' && 'is-mobile-submenu-open')}>
                                            <Link
                                                to="/download-app"
                                                className="header-nav-trigger"
                                                style={{ fontWeight: '600' }}
                                                aria-haspopup="true"
                                                aria-expanded={isMobile ? openMobileSubmenu === 'download' : undefined}
                                                onClick={(event) => {
                                                    if (toggleMobileSubmenu(event, 'download')) return;
                                                    closeMenu();
                                                }}
                                            >
                                                Download
                                                <DropdownChevron />
                                            </Link>
                                            <ul className="header-submenu header-download-submenu list-reset" aria-label="Download Luckynote">
                                                {DOWNLOAD_LINKS.map(({ label, description, to, icons }) => (
                                                    <li key={label}>
                                                        <Link to={to} onClick={closeMenu}>
                                                            <span className="header-download-icons" aria-hidden="true">
                                                                {icons.map((Icon, index) => (
                                                                    <Icon key={index} />
                                                                ))}
                                                            </span>
                                                            <span className="header-download-copy">
                                                                <span>{label}</span>
                                                                <small>{description}</small>
                                                            </span>
                                                        </Link>
                                                    </li>
                                                ))}
                                            </ul>
                                        </li>
                                        <li>
                                            <a
                                                href="/#pricing"
                                                style={{ fontWeight: '600' }}
                                                onClick={(e) => handleHashClick(e, 'pricing', 'PricingClick')}
                                            >
                                                Pricing
                                            </a>
                                        </li>
                                    </ul>
                                    <ul className="list-reset header-nav-right">
                                        <li>
                                            <Link
                                                to="/login"
                                                className="header-login-link"
                                                onClick={closeMenu}
                                                onMouseEnter={prefetchAppChunk}
                                                onFocus={prefetchAppChunk}
                                            >
                                                Log in
                                            </Link>
                                        </li>
                                        {!hideSignin && (
                                            <li>
                                                <Link
                                                    to="/register"
                                                    onClick={(event) => {
                                                        redirectToMobileStore(event);
                                                        closeMenu();
                                                    }}
                                                    className="button button-primary button-wide-mobile button-sm header-start-free"
                                                >
                                                    Start free
                                                </Link>
                                            </li>
                                        )}
                                    </ul>
                                </div>
                            </nav>
                        </>
                    )}
                </div>
            </div>
        </header>
    );
};

Header.propTypes = propTypes;
Header.defaultProps = defaultProps;

export default Header;
